/**
 * Permissões por perfil (D-013, plano do Módulo 1 §4).
 *
 * Esta tabela é a ÚNICA fonte da verdade:
 *  - a API usa para BLOQUEAR (403);
 *  - o site usa só para ESCONDER menus e botões.
 * "escopo" diz se a permissão vale para todo o sistema ou só para o município do usuário.
 */
import type { Perfil } from './perfis';

export const PERMISSOES = [
  'conta:usar',
  'municipios:gerenciar',
  'usuarios:gerenciar',
  'unidades:gerenciar',
  'acessos:ver',
  'sessoes:gerenciar',
  'auditoria:ver',
  'parametros:gerenciar',
  'solicitacoes:atender',
  // Módulo 2 (D-021, D-024)
  'pacientes:ver',
  'pacientes:cadastrar',
  'gestacoes:ver',
  'gestacoes:registrar',
  // Módulo 4 (D-027)
  'prenatal:ver',
  'prenatal:registrar',
  // Módulo 5 (proposta §3.5): médico, enfermeiro e gestor notificam
  'notificacoes:ver',
  'notificacoes:gerar',
  // Módulo 6 (D-035, D-037): só o médico confirma a conduta neonatal
  'maternidade:ver',
  'maternidade:registrar',
  'neonatologia:confirmar',
  // Módulo 7 (D-040 a D-044): só o médico encerra o seguimento
  'puericultura:ver',
  'puericultura:registrar',
  'puericultura:encerrar',
  // Módulo 8 (D-049)
  'agenda:ver',
  'agenda:exportar',
  'indicadores:ver',
  'denominadores:informar',
  // Módulo 9 (D-051, D-052)
  'anexos:ver',
  'anexos:enviar',
  'titular:atender',
  // Páginas Sífilis e HIV (D-060): visão da linha de cuidado por agravo, só leitura
  'agravos:ver',
] as const;

export type Permissao = (typeof PERMISSOES)[number];
export type Escopo = 'global' | 'municipio';

const TODOS: readonly Perfil[] = [
  'superadmin',
  'admin',
  'gestor',
  'gestor_regional',
  'medico',
  'enfermeiro',
  'recepcao',
  'visualizador',
];

/** Para cada permissão: quais perfis têm e com qual escopo. */
export const MATRIZ_PERMISSOES: Record<Permissao, Partial<Record<Perfil, Escopo>>> = {
  'conta:usar': Object.fromEntries(TODOS.map((p) => [p, 'global'])),
  'municipios:gerenciar': { superadmin: 'global' },
  'usuarios:gerenciar': { superadmin: 'global', admin: 'municipio' },
  'unidades:gerenciar': { superadmin: 'global', admin: 'municipio' },
  'acessos:ver': { superadmin: 'global' },
  'sessoes:gerenciar': { superadmin: 'global' },
  'auditoria:ver': { superadmin: 'global', admin: 'municipio', gestor: 'municipio' },
  'parametros:gerenciar': { superadmin: 'global' },
  'solicitacoes:atender': { superadmin: 'global', admin: 'municipio' },
  // "global" nos perfis clínicos = rede de municípios participantes: a linha de cuidado
  // atravessa UBS, maternidade e Casa da Gestante. Superadmin e admin não veem pacientes (D-013).
  'pacientes:ver': {
    medico: 'global',
    enfermeiro: 'global',
    recepcao: 'global',
    gestor: 'municipio',
  },
  'pacientes:cadastrar': { medico: 'global', enfermeiro: 'global', recepcao: 'global' },
  'gestacoes:ver': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  'gestacoes:registrar': { medico: 'global', enfermeiro: 'global' },
  'prenatal:ver': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  'prenatal:registrar': { medico: 'global', enfermeiro: 'global' },
  'notificacoes:ver': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  'notificacoes:gerar': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  'maternidade:ver': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  'maternidade:registrar': { medico: 'global', enfermeiro: 'global' },
  'neonatologia:confirmar': { medico: 'global' },
  'puericultura:ver': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  'puericultura:registrar': { medico: 'global', enfermeiro: 'global' },
  'puericultura:encerrar': { medico: 'global' },
  'agenda:ver': {
    medico: 'global',
    enfermeiro: 'global',
    recepcao: 'global',
    gestor: 'municipio',
  },
  'agenda:exportar': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  // Indicadores são sempre agregados; regional e visualizador não veem contagens pequenas
  'indicadores:ver': {
    medico: 'global',
    enfermeiro: 'global',
    gestor: 'municipio',
    gestor_regional: 'global',
    visualizador: 'global',
  },
  'denominadores:informar': { superadmin: 'global', gestor: 'municipio' },
  'anexos:ver': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  // O gestor envia só na notificação (a ficha assinada); a regra fina fica na API
  'anexos:enviar': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
  'titular:atender': { gestor: 'municipio' },
  'agravos:ver': { medico: 'global', enfermeiro: 'global', gestor: 'municipio' },
};

export function pode(perfil: Perfil, permissao: Permissao): boolean {
  return MATRIZ_PERMISSOES[permissao][perfil] !== undefined;
}

export function escopoDe(perfil: Perfil, permissao: Permissao): Escopo | undefined {
  return MATRIZ_PERMISSOES[permissao][perfil];
}

/**
 * Perfis que cada administrador pode atribuir.
 * O admin do município NÃO cria superadmin nem gestor regional (que enxerga vários municípios).
 */
export const PERFIS_ATRIBUIVEIS: Partial<Record<Perfil, readonly Perfil[]>> = {
  superadmin: TODOS,
  admin: ['admin', 'gestor', 'medico', 'enfermeiro', 'recepcao', 'visualizador'],
};

export function podeAtribuirPerfil(quemAtribui: Perfil, perfilAlvo: Perfil): boolean {
  return PERFIS_ATRIBUIVEIS[quemAtribui]?.includes(perfilAlvo) ?? false;
}
